import { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Loader2, Mail, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import InkPhDLogo from '@/components/InkPhDLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ThesisForgotPassword — Sprint 7 hotfix.
 *
 * Sends a POST to /api/auth/forgot-password with the user's email. Backend
 * generates a signed reset token, emails a link `${APP_URL}/reset-password?token=...`
 * via Resend, and returns success (even if the email doesn't exist — to
 * prevent user enumeration).
 */
export default function ThesisForgotPassword() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [devLink, setDevLink] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
      setSent(true);
      // If the deployment doesn't have email configured, backend returns
      // the reset link directly so the user isn't locked out.
      if (data?.dev_reset_link) setDevLink(data.dev_reset_link);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Unknown error';
      toast.error(`Could not send reset email: ${msg}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-stone-50 via-background to-background dark:from-slate-950">
      <header className="border-b border-border bg-card/80 backdrop-blur">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="forgot-logo-home">
            <InkPhDLogo size="sm" />
          </Link>
          <Link to="/thesis/login" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
            <ArrowLeft className="h-3 w-3" /> Back to sign in
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-10">
        <Card className="w-full max-w-md" data-testid="forgot-password-card">
          {!sent ? (
            <>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-serif text-2xl" style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
                  <Mail className="h-5 w-5 text-[hsl(212,90%,55%)]" />
                  Reset your password
                </CardTitle>
                <CardDescription>
                  Enter the email you used to sign up and we'll send you a secure link to set a new password.
                  The link expires in 1 hour.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={submit} className="space-y-4">
                  <div>
                    <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                      Email
                    </Label>
                    <Input
                      id="email" type="email" required autoFocus
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@university.edu"
                      className="mt-1"
                      data-testid="forgot-email-input"
                    />
                  </div>
                  <Button
                    type="submit" disabled={submitting}
                    className="w-full bg-slate-900 hover:bg-[hsl(212,90%,28%)] text-white"
                    data-testid="forgot-submit-btn">
                    {submitting
                      ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
                      : <>Send reset link</>}
                  </Button>
                  <p className="text-[11px] text-muted-foreground text-center pt-2 border-t border-border">
                    Remember your password?{' '}
                    <Link to="/thesis/login" className="text-[hsl(212,90%,38%)] hover:underline font-semibold">
                      Sign in
                    </Link>
                  </p>
                </form>
              </CardContent>
            </>
          ) : (
            <CardContent className="py-10 text-center" data-testid="forgot-sent-block">
              <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
              <h2 className="font-serif text-2xl mb-2" style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
                {devLink ? 'Copy your reset link' : 'Check your inbox'}
              </h2>
              {devLink ? (
                <>
                  <p className="text-sm text-muted-foreground mb-4">
                    Email delivery is not configured on this deployment.
                    Click the link below (or copy and paste it) to reset your password:
                  </p>
                  <div className="mb-4 p-3 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 text-left">
                    <a href={devLink}
                      className="text-xs font-mono text-[hsl(212,90%,38%)] hover:underline break-all"
                      data-testid="forgot-dev-link">
                      {devLink}
                    </a>
                    <Button size="sm" variant="ghost" className="mt-2 h-7 text-xs"
                      onClick={() => { navigator.clipboard.writeText(devLink); toast.success('Link copied'); }}
                      data-testid="forgot-copy-link">
                      Copy link
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground mb-6">
                    If an InkPhD account exists for <strong className="text-foreground">{email}</strong>,
                    you&apos;ll receive an email within a minute with a secure link to reset your password.
                  </p>
                  <p className="text-[11px] text-muted-foreground mb-6">
                    Didn&apos;t get anything? Check your spam folder, or wait 60&nbsp;s and try again.
                  </p>
                </>
              )}
              <div className="flex flex-col sm:flex-row gap-2 justify-center">
                <Button variant="outline" onClick={() => { setSent(false); setEmail(''); setDevLink(''); }} data-testid="forgot-try-again-btn">
                  Try another email
                </Button>
                <Button asChild className="bg-slate-900 hover:bg-[hsl(212,90%,28%)] text-white">
                  <Link to="/thesis/login">Back to sign in</Link>
                </Button>
              </div>
            </CardContent>
          )}
        </Card>
      </main>
    </div>
  );
}
